import { makeAutoObservable, runInAction } from "mobx";
import {
  DEFAULT_TIMELINE_SETTINGS,
  type TimelineGranularity,
  type TimelineGroupBy,
  type TimelineSettings,
} from "@/components/timeline/types";

const STORAGE_KEY = "timeline-preferences";

/** Per-user UI preferences for the global Timeline view, persisted to
 *  localStorage. Mirrors `ModuleListPreferencesStore`: window-guarded hydrate
 *  on construction, try/catch around JSON, and setters that persist. */
export class TimelinePreferencesStore {
  groupBy: TimelineGroupBy = "none";
  granularity: TimelineGranularity = "week";
  settings: TimelineSettings = { ...DEFAULT_TIMELINE_SETTINGS };

  constructor() {
    makeAutoObservable(this, {} as never, { autoBind: true });
    this.hydrate();
  }

  private hydrate(): void {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        groupBy?: TimelineGroupBy;
        granularity?: TimelineGranularity;
        settings?: Partial<TimelineSettings>;
      };
      runInAction(() => {
        if (parsed.groupBy) this.groupBy = parsed.groupBy;
        if (parsed.granularity) this.granularity = parsed.granularity;
        this.settings = { ...DEFAULT_TIMELINE_SETTINGS, ...parsed.settings };
      });
    } catch (err) {
      console.warn("[timeline-prefs] hydrate failed; resetting", err);
      runInAction(() => {
        this.groupBy = "none";
        this.granularity = "week";
        this.settings = { ...DEFAULT_TIMELINE_SETTINGS };
      });
    }
  }

  private persist(): void {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        groupBy: this.groupBy,
        granularity: this.granularity,
        settings: this.settings,
      }),
    );
  }

  setGroupBy(groupBy: TimelineGroupBy): void {
    this.groupBy = groupBy;
    this.persist();
  }

  setGranularity(granularity: TimelineGranularity): void {
    this.granularity = granularity;
    this.persist();
  }

  setSettings(settings: Partial<TimelineSettings>): void {
    this.settings = { ...this.settings, ...settings };
    this.persist();
  }
}
